<template>
  <div id="filter">
    <Button @click="isOpen = true" :disabled="searchMetadata?.count === 0" class="w-10 h-10 shadow-md">
      <span class="pi pi-filter"></span>
    </Button>
    <Drawer id="transactions-filter" position="left" z-index="20" v-model:visible="isOpen" shadow>
      <template #header>
        <div v-if="filterMetadataLoading" class="flex items-center">
          <Badge size="xlarge" severity="secondary">
            <ProgressSpinner strokeWidth="8" class="w-4 h-4" />
          </Badge>
          <Button plain text>{{ $t(`messages.loading`) }}</Button>
        </div>
        <div v-else>
          <Badge size="xlarge">{{ searchMetadata?.count }}</Badge>
          <Button variant="link" @click="clearFilters()" :disabled="filterMetadataLoading">
            {{ $t(`messages.transactions.clear_filters`) }}
          </Button>
        </div>
      </template>

      <form @submit.prevent="filterChange()">
        <div v-for="filter in filters" :key="filter.key" class="mb-2">
          <MultiSelect
            v-if="filter.type === 'multiselect'"
            menuClass="w-100"
            :multiple="true"
            :texts="texts"
            v-model="filter.value"
            :options="filter.options || []"
            :checkboxesStyle="true"
            :selectAllOption="true"
            option-label="label"
            option-value="value"
            fluid
            :disabled="filter.disabled"
            :placeholder="$t(`messages.transactions.fields.${filter.key}`)"
            @change="filterChange()"
            @update:model-value="(event) => console.log('event ASDASD', event, filter)"
          ></MultiSelect>

          <div v-else-if="filter.type === 'range' && !filter.disabled && isOpen" class="my-6">
            <label class="mb-2 flex items-center justify-between">
              <span class="text-sm">{{ $t(`messages.transactions.fields.${filter.key}`) }}</span>
              <span class="flex items-center gap-2">
                <Badge size="small">
                  {{ filter.value[0] ?? filter.options[0] }}
                </Badge>
                -
                <Badge size="small">
                  {{ filter.value[1] ?? filter.options[1] }}
                </Badge>
              </span>
            </label>
            <Slider
              v-model="filter.value"
              :min="filter.options[0]"
              :max="filter.options[1]"
              :step="filter.options[1] % filter.options[0] === 0 ? 1 : 0.0001"
              :disabled="filter.disabled"
              range
              tooltip="none"
              @slideend="filterChange()"
              @change="filterChange()"
            />
          </div>

          <div v-else-if="filter.type === 'switch'" class="my-6">
            <label class="mb-2 flex items-center justify-between">
              <ToggleSwitch
                v-model="filter.value"
                :disabled="filter.disabled"
                @change="filterChange()"
              />
              <span class="text-sm">{{ $t(`messages.transactions.fields.${filter.key}`) }}</span>
            </label>
          </div>

          <div
            v-else-if="filter.type === 'search'"
            :id="`transaction-filter-${filter.key}`"
            :label="
              filter.label ? $t(filter.label) : $t(`messages.transactions.fields.${filter.key}`)
            "
            :label-for="`transaction-filter-${filter.key}-input`"
            :invalid-feedback="$t(`messages.transactions.filters.${filter.key}.feedback`)"
            :state="getSearchFilterState(filter)"
          >
            <PredefinedValuesInputComponent
              type="search"
              category="transactions"
              name="filter_markused"
              :id="`transaction-filter-${filter.key}-input`"
              v-model="filter.value"
              @blur="filterChange()"
              @selected="filterChange()"
              :state="getSearchFilterState(filter)"
              menu-class="dropdown-max-width"
            />
          </div>
        </div>
      </form>
    </Drawer>
  </div>
</template>

<script setup>
import { debounce } from 'lodash-es'
import { storeToRefs } from 'pinia'
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { TRANSACTIONS_ADDITIONAL_FILTERS } from '@/constants'
import { naturalSort } from '@/services/utilities'
import { useTransactionStore } from '@/stores/transaction'
import PredefinedValuesInputComponent from '../PredefinedValuesInputComponent.vue'

const transactionStore = useTransactionStore()
const {
  searchMetadata,
  filterMetadataLoading,
  transactionMapRequestParams,
  shouldInitTransactionFilters,
} = storeToRefs(transactionStore)

const { t } = useI18n()

const emit = defineEmits(['update:map-features'])

const isOpen = ref(false)
const filters = ref([])
const texts = ref({
  selectvalue: `${t('messages.transactions.choose')}...`,
  optionsselected: 'option(s) selected',
  nothingselected: 'No option selected',
  selectall: t('messages.transactions.choose_all'),
  all: t('messages.transactions.all'),
  selected: t('messages.transactions.chosen'),
  placeholder: `${t('messages.transactions.filter')}...`,
})

const initFilters = async () => {
  buildFiltersFromMetadata(searchMetadata.value)
}

const buildFiltersFromMetadata = metadata => {
  filters.value = []
  TRANSACTIONS_ADDITIONAL_FILTERS.forEach(filter => {
    const filterItem = {
      ...{
        value: [],
        options: [],
        disabled: false,
      },
      ...filter,
    }

    let options = []
    if (metadata?.filterOptions[filter.key]) {
      options = metadata.filterOptions[filter.key]
    }
    if (metadata?.ranges[filter.key]) {
      options = metadata.ranges[filter.key]
    }

    if (filter.type === 'multiselect') {
      filterItem.disabled = options.length < 2
      filterItem.options = options.map(item => ({
        value: item,
        label: item,
      }))
      filterItem.value = []
    } else if (filter.type === 'range') {
      filterItem.disabled = options['min'] === options['max']
      filterItem.options = [Number(options['min']), Number(options['max'])]
      filterItem.value = [null, null]
    } else if (filter.type === 'switch') {
      filterItem.value = false
    }

    filters.value.push(filterItem)
  })
}

const getSearchFilterState = filter => {
  const state = filter.value?.length > 0 && filter.value.length < filter.minChar

  return state ? !state : null
}

const filterChange = debounce(() => {
  transactionStore.abortAndResetTransactionsAbortController()

  const currentFilters = Object.entries(
    transactionMapRequestParams.value.filters || {},
  ).reduce((acc, [key, value]) => {
    const isAdditionalFilter = TRANSACTIONS_ADDITIONAL_FILTERS.some(
      f => f.key === key,
    )
    if (!isAdditionalFilter) {
      acc[key] = value
    }
    return acc
  }, {})

  const activeFilters = filters.value.reduce((acc, filter) => {
    let isActive = false

    if (filter.type === 'multiselect') {
      isActive = !!filter.value.length
    } else if (filter.type === 'range') {
      const hasMin = filter.value[0] !== undefined && filter.value[0] !== null
      const hasMax = filter.value[1] !== undefined && filter.value[1] !== null
      isActive = hasMin || hasMax

      if (isActive) {
        const min = hasMin ? filter.value[0] : filter.options[0]
        const max = hasMax ? filter.value[1] : filter.options[1]
        filter.value = [min, max]
      }
    } else if (filter.type === 'switch') {
      isActive = filter.value // Only add if true
    } else if (filter.type === 'search') {
      isActive = filter.value.length >= filter.minChar
    }

    if (isActive) {
      acc[filter.key] = {
        key: filter.key,
        value: filter.value,
        type: filter.type,
      }
    }
    return acc
  }, {})

  transactionMapRequestParams.value.filters = {
    ...currentFilters,
    ...activeFilters,
  }

  emit('update:map-features')
}, 300)

const clearFilters = () => {
  const currentFilters = transactionMapRequestParams.value.filters || {}

  const filtersToKeep = Object.entries(currentFilters).reduce(
    (acc, [key, value]) => {
      const isAdditionalFilter = TRANSACTIONS_ADDITIONAL_FILTERS.some(
        f => f.key === key,
      )

      if (!isAdditionalFilter) {
        acc[key] = value
      }

      return acc
    },
    {},
  )

  transactionMapRequestParams.value.filters = filtersToKeep

  emit('update:map-features')

  shouldInitTransactionFilters.value = true
}

watch(searchMetadata, () => {
  if (shouldInitTransactionFilters.value) {
    initFilters()
    shouldInitTransactionFilters.value = false
  }
})

defineExpose({
  clearFilters,
})
</script>
